/* =========================
   FILE: src/App.jsx
   DROP-IN REPLACEMENT
   ========================= */
import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';

// project imports
import router from 'routes';
import ThemeCustomization from 'themes';
import ScrollTop from 'components/ScrollTop';
import VpHelpChat from "./chat/VpHelpChat";


import { useAutoLogout } from 'utils/useAutoLogout';

function AutoLogoutGate() {
  // Track token changes even after login (same tab + other tabs)
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  useEffect(() => {
    const sync = () => setToken(localStorage.getItem('token'));

    // changes from OTHER tabs/windows
    window.addEventListener('storage', sync);

    // changes in THIS tab
    const poll = setInterval(sync, 500);

    return () => {
      window.removeEventListener('storage', sync);
      clearInterval(poll);
    };
  }, []);

  // ✅ When token becomes set (login), reset last active so you don't instantly auto-logout
  useEffect(() => {
    if (token) {
      try {
        localStorage.setItem('vp_last_active_at', String(Date.now()));
      } catch {
        // ignore
      }
    }
  }, [token]);

  const isPublicRoute = () => {
    const p = (window.location.pathname || '').toLowerCase();
    return p === '/' || p.includes('/login') || p.includes('/activate') || p.includes('/setup-totp');
  };

  const doLogout = () => {
    // Clear auth
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Disable hook immediately (same tab)
    setToken(null);

    // Avoid redirect loops on public routes
    if (isPublicRoute()) return;

    const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
    window.location.href = `${base}/login`;
  };

  // ✅ TEST SETTINGS (switch to prod later)
  //const idleMs = 20 * 1000; // 20 seconds idle
  //const warnMs = 10 * 1000; // 10 second countdown

  // ✅ PRODUCTION SETTINGS
  const idleMs = 5 * 60 * 1000;  // 5 minutes total inactivity
  const warnMs = 1 * 60 * 1000;  // show countdown at 1 minute remaining

  const { warningOpen, secondsLeft, stayLoggedIn } = useAutoLogout({
    enabled: !!token && !isPublicRoute(),
    idleMs,
    warnMs,
    onLogout: doLogout
  });

  return (
    <Dialog open={warningOpen} onClose={stayLoggedIn} maxWidth="xs" fullWidth>
      <DialogTitle>Session Expiring</DialogTitle>
      <DialogContent dividers>
        <Typography>
          You’ve been inactive. You will be logged out in <b>{secondsLeft}</b> seconds.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={doLogout} color="error" variant="outlined">
          Log out now
        </Button>
        <Button onClick={stayLoggedIn} variant="contained">
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function App() {
  return (
    <ThemeCustomization>
      <ScrollTop>
        <RouterProvider router={router} />
        <AutoLogoutGate />
        <VpHelpChat />
      </ScrollTop>
    </ThemeCustomization>
  );
}
